import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Slide, ToastContainer } from "react-toastify";
import { UnauthorizedSessionBridge } from "@/lib/auth/UnauthorizedSessionBridge";
import { SessionProvider } from "@/context/SessionProvider";
import { ThemeProvider, useTheme } from "@/context/ThemeProvider";
import type { RouterContext } from "@/routing/guards";
import { RoutePending } from "@/routing/ui/RoutePending";

const TanStackRouterDevtools = import.meta.env.PROD
  ? null
  : lazy(() =>
    import("@tanstack/react-router-devtools").then((d) => ({
      default: d.TanStackRouterDevtools,
    }))
  );

/** Single toast host for authenticated and guest routes. */
function AppToastContainer() {
  const { darkMode } = useTheme();

  return (
    <ToastContainer
      position="bottom-right"
      autoClose={2000}
      hideProgressBar={false}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable
      pauseOnHover
      transition={Slide}
      theme={darkMode ? "dark" : "light"}
    />
  );
}

function RootComponent() {
  return (
    <SessionProvider>
      <ThemeProvider>
        {/* App wiring: queryClient 401 → toast + completeSignOut */}
        <UnauthorizedSessionBridge />
        <AppToastContainer />
        <Suspense fallback={<RoutePending />}>
          <Outlet />
        </Suspense>
        {TanStackRouterDevtools && (
          <Suspense fallback={null}>
            <TanStackRouterDevtools />
          </Suspense>
        )}
      </ThemeProvider>
    </SessionProvider>
  );
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});
